import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import React from 'react'
import styled from 'styled-components'
import { semantic } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'

export interface TooltipProps {
  content: React.ReactNode
  // The element the tooltip anchors to. It becomes the trigger itself (no
  // wrapper element), so it must accept a ref and spread unknown props onto
  // its DOM node.
  children: React.ReactElement
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  // Milliseconds before a hovered trigger opens the tooltip.
  delay?: number
  closeDelay?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  zIndex?: number
  // Lands on the popup, so `styled(Tooltip)` overrides its box.
  className?: string
}

// Above antd modals (1000), popovers (1030) and dropdowns (1050), which the
// tooltip has to clear while antd components are still in the tree.
const DEFAULT_Z_INDEX = 1070

const isEmptyContent = (content: React.ReactNode): boolean =>
  content === null ||
  content === undefined ||
  typeof content === 'boolean' ||
  content === '' ||
  (Array.isArray(content) && content.length === 0)

const Popup = styled(BaseTooltip.Popup)`
  box-sizing: border-box;
  max-width: 250px;
  max-height: 500px;
  overflow-y: auto;
  padding: 6px 8px;
  color: ${semantic.fgOnDark};
  background: ${semantic.bgDark};
  border-radius: ${radii.xs}px;
  box-shadow: ${shadows.md};
  font-size: 12px;
  line-height: 18px;
  text-align: start;
  overflow-wrap: break-word;
`

export const Tooltip = ({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  delay = 0,
  closeDelay,
  open,
  onOpenChange,
  disabled = false,
  zIndex = DEFAULT_Z_INDEX,
  className,
}: TooltipProps): React.ReactElement => (
  // Always the same tree, so a tooltip whose content toggles between empty
  // and set never remounts its trigger.
  <BaseTooltip.Root
    open={open}
    onOpenChange={onOpenChange && ((next) => onOpenChange(next))}
    disabled={disabled || isEmptyContent(content)}
  >
    <BaseTooltip.Trigger
      render={children}
      delay={delay}
      closeDelay={closeDelay}
    />
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        style={{ zIndex }}
      >
        <Popup className={className}>{content}</Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  </BaseTooltip.Root>
)

Tooltip.displayName = 'Tooltip'
